/**
 * SOURCE UNIQUE DE LA PALETTE ZENKART.
 *
 * Avant le 2026-09-03, ces valeurs étaient redéclarées à la main dans 39
 * fichiers (301 déclarations, 415 hex en dur). Changer une couleur de charte
 * demandait donc 39 éditions, et le texte du site était à `#1A1A1A` partout
 * alors que la charte dit `#1A1814` — l'écart avait survécu à tout le monde.
 *
 * Règle : aucune couleur en dur dans un composant. On importe d'ici.
 *
 * Le pendant CSS est `tokens.css`, qui déclare les mêmes valeurs en variables
 * `--zk-*` pour les feuilles de style. Les deux fichiers sont tenus synchrones
 * par `tokens.test.js`, qui échoue si l'un dérive de l'autre — c'est ce test
 * qui rend la « source unique » vraie plutôt que déclarative.
 */

/* ── Fonds et texte ───────────────────────────────────────────────────────── */
export const CREAM = '#F3EEE6';   // fond de toutes les pages
export const WHITE = '#FFFFFF';
/** Charte : #1A1814, une encre légèrement chaude — PAS #1A1A1A (gris neutre). */
export const TEXT  = '#1A1814';
export const MUTED = '#6B6258';   // texte secondaire

/* ── Bleu : couleur d'identité et d'action principale ─────────────────────── */
export const BLUE      = '#1B4D8F';
export const BLUE_DARK = '#153D72';           // survol / état pressé
export const BLUE_L    = '#E8EFF8';           // fond de zone bleue
export const BORDER    = 'rgba(27,77,143,0.15)';
export const BORDER_H  = 'rgba(27,77,143,0.35)';

/* ── Orange : accent d'enquête, jamais une valeur monétaire ───────────────── */
export const ORANGE   = '#E06A1A';
export const ORANGE_D = '#C55A11';
export const ORG_L    = '#FCF0E6';

/* ── Vert : RÉSERVÉ à l'argent (marge, économie, gain) ────────────────────── */
// Trois verts coexistaient : #159247 (charte, 20 fichiers), #1E7A4B
// (ContactPage) et #22c55e (JeunePermisPage, reste du thème sombre).
// Un seul survit, et c'est celui de la charte.
export const GREEN   = '#159247';
export const GRN_L   = '#E8F5EC';
export const GREEN_L = 'rgba(21,146,71,0.08)';   // dérivé de GREEN, plus de 34,197,94
export const GREEN_B = 'rgba(21,146,71,0.20)';

/* ── Signal ───────────────────────────────────────────────────────────────── */
export const AMBER    = '#E0B43C';
// Ambre assombri : le #E0B43C ne passe pas en TEXTE sur le crème (contraste
// ~1,9:1). Il sert donc de pastille et de fond, jamais de lettre. Cette
// version foncée est la seule autorisée pour un CHIFFRE défavorable —
// au-dessus du marché, frais à prévoir — parce que l'orange est un accent
// d'enquête et ne doit jamais porter un montant.
export const AMBER_D  = '#7A5F12';
// #B3261E et #B81E1E cohabitaient pour le même usage (erreur / danger).
export const RED      = '#B3261E';
export const RED_BG   = 'rgba(179,38,30,0.06)';
export const RED_BORD = 'rgba(179,38,30,0.22)';

/* ── Typographie ──────────────────────────────────────────────────────────────
   DEUX RÔLES, DEUX POLICES (2026-09-10).

   `FONT` porte le texte : Instrument Sans, une grotesque humaniste sobre.
   `FONT_DISPLAY` porte les TITRES ET LES CHIFFRES : Archivo, dont le squelette
   plus mécanique et les chiffres tabulaires font exactement ce qu'on demande à
   ce produit — aligner des montants et les rendre lisibles d'un coup d'œil.

   Elles remplacent Inter et Barlow Condensed. Barlow Condensed était une
   condensée de titre : elle ne sait pas tenir une colonne de prix, et c'est
   pourtant ce que la refonte lui demandait. */
export const FONT = "'Instrument Sans', system-ui, sans-serif";
export const FONT_DISPLAY = "'Archivo', 'Instrument Sans', system-ui, sans-serif";

/* ── Échelle de texte ─────────────────────────────────────────────────────────
   NEUF valeurs, là où le site en comptait TRENTE (mesuré le 2026-09-10 : 823
   `fontSize` en ligne, dont 13px 189 fois, 14px 105 fois, 11px 105 fois, plus
   12,5 / 13,5 / 11,5 / 14,5 / 15,5 / 16,5 semés au fil des pages).

   Les noms disent le RÔLE et pas la taille : c'est ce qui empêche le prochain
   « il me faudrait juste un 13,5 ici ». Si un rôle manque, on l'ajoute ici. */
export const TYPE = {
  micro:   11,     // étiquettes en capitales, mentions légales
  petit:   12.5,   // caractéristiques, légendes, aide de champ
  corps:   14,     // texte d'interface courant
  lecture: 15.5,   // paragraphes qu'on lit vraiment
  t3:      17,     // titre de carte
  t2:      21,     // titre de section
  t1:      27,     // titre de page
  affiche: 34,     // héros
  geant:   44,     // le chiffre du verdict, et lui seul
};

/* Hauteurs de ligne, appariées aux rôles ci-dessus. */
export const LH = {
  serre:  1.15,   // affiche, geant
  titre:  1.25,   // t1, t2, t3
  normal: 1.5,    // corps, petit, micro
  aere:   1.6,    // lecture
};

/* ── Échelle d'espacement ─────────────────────────────────────────────────────
   Base 4. Les 476 `padding` en ligne du site n'obéissaient à aucune échelle ;
   toute valeur intermédiaire inventée sur place se voit en comparaison. */
export const SPACE = {
  1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 40, 9: 56, 10: 72,
};

/* ── Rayons ───────────────────────────────────────────────────────────────────
   TROIS, contre quinze mesurés (8, 10, 6, 12, 14, 2, 99, 3, 16, 4, 20, 999,
   9, 1…). Un rayon dit « objet séparé » ; quinze rayons ne disent plus rien. */
export const RADIUS = { petit: 8, moyen: 12, grand: 16, pill: 999 };

/* Anciens noms, conservés le temps de la reprise page par page : ils pointent
   sur l'échelle, ils n'ont plus de valeur propre. */
export const RADIUS_BTN  = RADIUS.petit;
export const RADIUS_CARD = RADIUS.moyen;
export const RADIUS_PILL = RADIUS.pill;

/* ── Ombres ───────────────────────────────────────────────────────────────────
   TROIS niveaux, et une encre CHAUDE au lieu du bleu : une ombre bleutée sur
   un fond crème se lit comme un halo colore, pas comme une ombre.

   Un bouton n'a plus d'ombre du tout (`plat`). C'est la conséquence directe de
   « aucun ornement » : l'ombre portée bleue des boutons était le dernier reste
   d'un vocabulaire d'interface qui cherchait à impressionner. */
export const SHADOW = {
  plat: 'none',
  pose: '0 1px 2px rgba(26,24,20,0.06)',
  leve: '0 6px 20px rgba(26,24,20,0.10)',
};

export const SHADOW_BTN         = SHADOW.plat;
export const SHADOW_BTN_PRESSED = SHADOW.plat;
export const SHADOW_CARD        = SHADOW.pose;
export const SHADOW_CARD_HOVER  = SHADOW.leve;
